import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { useAuth } from "../context/AuthContext";
import { Receipt, Clock, CheckCheck, Package, Ban, MessageCircle, Sparkles, MoreVertical, EyeOff, RotateCcw, LifeBuoy, ChefHat, Truck, Flame } from "lucide-react";
import { toast } from "sonner";
import OrderChatSheet from "../components/OrderChatSheet";
import UserGem from "../components/UserGem";
import SmartImage from "../components/SmartImage";
import LiveTrackingStrip from "../components/LiveTrackingStrip";
import GetHelpDialog from "../components/GetHelpDialog";
import { useCart } from "../context/CartContext";

const STATUS_META = {
  pending_payment: { label: "Pending payment", icon: Clock, color: "text-amber-300 bg-amber-500/10 border-amber-500/30" },
  paid: { label: "Paid", icon: CheckCheck, color: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30" },
  // Phase 4 lifecycle statuses
  new: { label: "New", icon: Flame, color: "text-pink-300 bg-pink-500/10 border-pink-500/30" },
  accepted: { label: "Accepted", icon: CheckCheck, color: "text-amber-300 bg-amber-500/10 border-amber-500/30" },
  preparing: { label: "Preparing", icon: ChefHat, color: "text-cyan-300 bg-cyan-500/10 border-cyan-500/30" },
  ready: { label: "Ready", icon: Package, color: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30" },
  out_for_delivery: { label: "On the way", icon: Truck, color: "text-violet-300 bg-violet-500/10 border-violet-500/30" },
  // Legacy
  shipped: { label: "Shipped", icon: Package, color: "text-cyan-300 bg-cyan-500/10 border-cyan-500/30" },
  completed: { label: "Completed", icon: CheckCheck, color: "text-pink-300 bg-pink-500/10 border-pink-500/30" },
  cancelled: { label: "Cancelled", icon: Ban, color: "text-red-300 bg-red-500/10 border-red-500/30" },
};

const ACTIVE_BUYER_STATUSES = new Set(["new", "accepted", "preparing", "ready", "out_for_delivery", "pending_payment", "paid", "shipped"]);

function OrderCard({ o, role, onUpdateStatus, onOpenChat, onSharePost, onHide, onUnhide, isHidden, onReorder, onGetHelp }) {
  const navigate = useNavigate();
  const meta = STATUS_META[o.status] || STATUS_META.pending_payment;
  const Icon = meta.icon;
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmHide, setConfirmHide] = useState(false);
  const canHide = ["completed", "cancelled"].includes(o.status);
  const showTracking = role === "buyer" && ACTIVE_BUYER_STATUSES.has(o.status);
  const canReorder = role === "buyer" && ["completed", "cancelled"].includes(o.status);
  const canGetHelp = role === "buyer" && o.status !== "pending_payment";
  return (
    <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-4 space-y-3 relative" data-testid={`order-${o.order_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
            {new Date(o.created_at).toLocaleString()}
          </p>
          <p className="font-bold mt-0.5 inline-flex items-center gap-1">
            {role === "buyer" ? `From @${o.vendor_name}` : `To ${o.buyer_name}`}
            <UserGem userId={role === "buyer" ? o.vendor_id : o.buyer_id} size="xs" />
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${meta.color}`}>
            <Icon className="w-3 h-3" /> {meta.label}
          </span>
          {(canHide || isHidden) && (
            <div className="relative">
              <button
                onClick={() => setMenuOpen((v) => !v)}
                className="h-7 w-7 grid place-items-center rounded-full hover:bg-white/10 text-zinc-400"
                data-testid={`order-menu-btn-${o.order_id}`}
                aria-label="Order options"
              >
                <MoreVertical className="w-4 h-4" />
              </button>
              {menuOpen && (
                <div
                  className="absolute right-0 top-9 z-20 w-56 rounded-xl border border-white/10 bg-zinc-900 shadow-2xl overflow-hidden"
                  data-testid={`order-menu-${o.order_id}`}
                >
                  {isHidden ? (
                    <button
                      onClick={() => { setMenuOpen(false); onUnhide?.(o); }}
                      className="w-full text-left px-3 py-2.5 text-sm hover:bg-white/5 inline-flex items-center gap-2"
                      data-testid={`order-unhide-btn-${o.order_id}`}
                    >
                      <RotateCcw className="w-4 h-4 text-cyan-300" /> Restore to my orders
                    </button>
                  ) : (
                    <button
                      onClick={() => { setMenuOpen(false); setConfirmHide(true); }}
                      className="w-full text-left px-3 py-2.5 text-sm hover:bg-white/5 inline-flex items-center gap-2"
                      data-testid={`order-hide-btn-${o.order_id}`}
                    >
                      <EyeOff className="w-4 h-4 text-red-300" /> Remove from my orders
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {confirmHide && (
        <div className="rounded-xl border border-red-400/30 bg-red-500/10 p-3 text-xs space-y-2" data-testid={`order-hide-confirm-${o.order_id}`}>
          <p className="text-red-100">
            Remove this order from your visible history? Your financial records, receipts, and vendor copy are kept safe. You can restore it later from <b>Hidden orders</b>.
          </p>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setConfirmHide(false)}
              className="h-8 px-3 rounded-full bg-white/5 border border-white/10 text-xs font-bold"
              data-testid={`order-hide-cancel-${o.order_id}`}
            >
              Cancel
            </button>
            <button
              onClick={() => { setConfirmHide(false); onHide?.(o); }}
              className="h-8 px-3 rounded-full bg-red-500 hover:bg-red-400 text-white text-xs font-bold"
              data-testid={`order-hide-confirm-btn-${o.order_id}`}
            >
              Remove
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {o.items.map((it) => {
          const cs = it.customization_summary || null;
          const hasCustom = Boolean(cs && (cs.variation || (cs.modifiers || []).length || (cs.ingredients || []).length || cs.special_instructions));
          return (
            <div key={it.line_id || it.product_id} className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-lg bg-zinc-900 overflow-hidden shrink-0">
                {it.image_paths?.[0] && (
                  <SmartImage src={fileUrl(it.image_paths[0])} alt={it.name} mode="smart_auto_fit" className="w-full h-full" testid={`order-item-image-${it.product_id}`} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{it.name}</p>
                <p className="text-xs text-zinc-400">× {it.quantity} · ${it.price.toFixed(2)}</p>
                {hasCustom && (
                  <div className="mt-1 text-[11px] text-zinc-400 space-y-0.5" data-testid={`order-line-custom-${it.line_id || it.product_id}`}>
                    {cs.variation && <div className="truncate">Size: {cs.variation}</div>}
                    {(cs.modifiers || []).length > 0 && <div className="truncate">+ {cs.modifiers.join(", ")}</div>}
                    {(cs.ingredients || []).length > 0 && <div className="truncate text-amber-300/80">{cs.ingredients.join(" · ")}</div>}
                    {cs.special_instructions && <div className="italic text-pink-300 truncate">"{cs.special_instructions}"</div>}
                  </div>
                )}
              </div>
              <p className="text-sm font-bold whitespace-nowrap">${it.line_total.toFixed(2)}</p>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-white/10">
        <div>
          <p className="text-xs text-zinc-400">Pay via {o.payment_method}</p>
          {o.payment_handle && (
            <p className="text-sm font-bold text-pink-300">{o.payment_handle}</p>
          )}
        </div>
        <p className="text-2xl font-black">${o.total.toFixed(2)}</p>
      </div>

      {showTracking && (
        <div className="rounded-xl bg-white/[0.02] border border-white/5 p-3" data-testid={`tracking-wrap-${o.order_id}`}>
          <LiveTrackingStrip orderId={o.order_id} />
        </div>
      )}

      <button
        onClick={() => onOpenChat(o)}
        className="w-full h-10 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-200 text-sm font-semibold inline-flex items-center justify-center gap-2"
        data-testid={`order-chat-btn-${o.order_id}`}
      >
        <MessageCircle className="w-4 h-4" /> Chat with {role === "buyer" ? `@${o.vendor_name}` : o.buyer_name}
      </button>

      {role === "buyer" && ["paid", "shipped", "completed", "accepted", "preparing", "ready", "out_for_delivery", "cancelled"].includes(o.status) && (
        <button
          onClick={() => navigate(`/orders/${o.order_id}/receipt`)}
          className="text-xs text-zinc-400 hover:text-pink-300 underline-offset-2 hover:underline"
          data-testid={`receipt-link-${o.order_id}`}
        >
          View receipt
        </button>
      )}

      {role === "buyer" && (canReorder || canGetHelp) && (
        <div className="flex gap-2" data-testid={`buyer-actions-${o.order_id}`}>
          {canReorder && (
            <button
              onClick={() => onReorder(o)}
              className="flex-1 h-10 rounded-full bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-200 border border-emerald-500/30 text-sm font-bold inline-flex items-center justify-center gap-2"
              data-testid={`reorder-btn-${o.order_id}`}
            >
              <RotateCcw className="w-4 h-4" /> Reorder
            </button>
          )}
          {canGetHelp && (
            <button
              onClick={() => onGetHelp(o)}
              className={`flex-1 h-10 rounded-full ${o.has_issue ? "bg-amber-500/20 text-amber-100 border-amber-500/40" : "bg-white/5 text-zinc-200 border-white/10 hover:bg-white/10"} border text-sm font-bold inline-flex items-center justify-center gap-2`}
              data-testid={`get-help-btn-${o.order_id}`}
            >
              <LifeBuoy className="w-4 h-4" /> {o.has_issue ? "Issue reported" : "Get help"}
            </button>
          )}
        </div>
      )}

      {role === "vendor" && o.status !== "completed" && o.status !== "cancelled" && (
        <div className="flex gap-2 pt-2">
          {o.status === "pending_payment" && (
            <button
              onClick={() => onUpdateStatus(o.order_id, "paid")}
              className="flex-1 h-10 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-bold"
              data-testid={`mark-paid-${o.order_id}`}
            >
              Mark as paid
            </button>
          )}
          {o.status === "paid" && (
            <button
              onClick={() => onUpdateStatus(o.order_id, "shipped")}
              className="flex-1 h-10 rounded-full bg-cyan-500 hover:bg-cyan-400 text-black text-sm font-bold"
              data-testid={`mark-shipped-${o.order_id}`}
            >
              Mark as shipped
            </button>
          )}
          {o.status === "shipped" && (
            <button
              onClick={() => onUpdateStatus(o.order_id, "completed")}
              className="flex-1 h-10 rounded-full bg-pink-500 hover:bg-pink-400 text-white text-sm font-bold"
              data-testid={`mark-completed-${o.order_id}`}
            >
              Mark complete
            </button>
          )}
        </div>
      )}

      {role === "buyer" && o.status === "pending_payment" && (
        <button
          onClick={() => onUpdateStatus(o.order_id, "cancelled")}
          className="w-full h-10 rounded-full bg-white/5 border border-white/10 text-zinc-300 hover:bg-white/10 text-sm font-semibold"
          data-testid={`cancel-order-${o.order_id}`}
        >
          Cancel order
        </button>
      )}

      {role === "buyer" && ["paid", "shipped", "completed"].includes(o.status) && o.items?.[0]?.product_id && (
        <button
          onClick={() => onSharePost?.(o)}
          className="w-full h-10 rounded-full bg-pink-500/15 hover:bg-pink-500/25 text-pink-200 border border-pink-400/30 text-sm font-bold inline-flex items-center justify-center gap-2"
          data-testid={`share-post-${o.order_id}`}
        >
          <Sparkles className="w-3.5 h-3.5" /> Share a post about this
        </button>
      )}
    </div>
  );
}

export default function Orders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("buying");
  const [buyerOrders, setBuyerOrders] = useState([]);
  const [vendorOrders, setVendorOrders] = useState([]);
  const [hiddenOrders, setHiddenOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatOrder, setChatOrder] = useState(null);
  const [helpOrder, setHelpOrder] = useState(null);
  const { refresh: refreshCart, setOpen: openCartSheet } = useCart();

  const reorder = async (order) => {
    try {
      const { data } = await api.post(`/orders/${order.order_id}/reorder`);
      await refreshCart();
      if (data.skipped_count > 0) {
        const names = data.skipped.slice(0, 2).map((s) => s.name).join(", ");
        toast.message(`${data.added_count} added · ${data.skipped_count} unavailable (${names})`);
      } else {
        toast.success(`${data.added_count} items added back to your bag`);
      }
      openCartSheet(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reorder");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const calls = [
        api.get("/orders", { params: { role: "buyer" } }),
        api.get("/orders/hidden"),
      ];
      if (user?.is_vendor) calls.push(api.get("/orders", { params: { role: "vendor" } }));
      const results = await Promise.all(calls);
      setBuyerOrders(results[0].data);
      setHiddenOrders(results[1].data);
      if (user?.is_vendor) setVendorOrders(results[2].data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onUpdateStatus = async (id, status) => {
    try {
      await api.post(`/orders/${id}/status`, null, { params: { status } });
      toast.success("Updated");
      load();
    } catch {
      toast.error("Couldn't update order");
    }
  };

  const onHide = async (o) => {
    try {
      await api.post(`/orders/${o.order_id}/hide`);
      toast.success("Order removed from your history");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't remove");
    }
  };

  const onUnhide = async (o) => {
    try {
      await api.post(`/orders/${o.order_id}/unhide`);
      toast.success("Order restored");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't restore");
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-5" data-testid="orders-page">
      <div className="flex items-center gap-3 mb-5">
        <Receipt className="w-7 h-7 text-pink-400" />
        <h1 className="text-3xl font-black tracking-tighter">Orders</h1>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white/5 border border-white/10 rounded-full p-1">
          <TabsTrigger
            value="buying"
            className="rounded-full data-[state=active]:bg-pink-500 data-[state=active]:text-white px-5"
            data-testid="orders-tab-buying"
          >
            Buying
          </TabsTrigger>
          {user?.is_vendor && (
            <TabsTrigger
              value="selling"
              className="rounded-full data-[state=active]:bg-pink-500 data-[state=active]:text-white px-5"
              data-testid="orders-tab-selling"
            >
              Selling
            </TabsTrigger>
          )}
          <TabsTrigger
            value="hidden"
            className="rounded-full data-[state=active]:bg-pink-500 data-[state=active]:text-white px-5"
            data-testid="orders-tab-hidden"
          >
            Hidden {hiddenOrders.length > 0 && <span className="ml-1 text-[10px] opacity-70">({hiddenOrders.length})</span>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="buying" className="mt-4 space-y-3">
          {loading && <p className="text-zinc-500 text-sm">Loading…</p>}
          {!loading && hiddenOrders.length > 0 && (
            <button
              onClick={() => setTab("hidden")}
              className="w-full text-left rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] px-3 py-2 text-xs text-zinc-400 inline-flex items-center justify-between transition-colors"
              data-testid="hidden-orders-chip"
            >
              <span>
                <span className="font-black text-white">{hiddenOrders.length}</span> hidden
              </span>
              <span className="font-bold text-pink-300">Show →</span>
            </button>
          )}
          {!loading && buyerOrders.length === 0 && (
            <div className="text-center py-16 text-zinc-500" data-testid="no-buying-orders">
              No orders yet. Hit the feed and find something good.
            </div>
          )}
          {buyerOrders.map((o) => (
            <OrderCard
              key={o.order_id}
              o={o}
              role="buyer"
              onUpdateStatus={onUpdateStatus}
              onOpenChat={setChatOrder}
              onSharePost={(order) => navigate(`/create-post?product_id=${order.items?.[0]?.product_id || ""}`)}
              onHide={onHide}
              onReorder={reorder}
              onGetHelp={setHelpOrder}
            />
          ))}
        </TabsContent>

        {user?.is_vendor && (
          <TabsContent value="selling" className="mt-4 space-y-3">
            {loading && <p className="text-zinc-500 text-sm">Loading…</p>}
            {!loading && vendorOrders.length === 0 && (
              <div className="text-center py-16 text-zinc-500" data-testid="no-selling-orders">
                No incoming orders yet.
              </div>
            )}
            {vendorOrders.map((o) => (
              <OrderCard
                key={o.order_id}
                o={o}
                role="vendor"
                onUpdateStatus={onUpdateStatus}
                onOpenChat={setChatOrder}
                onHide={onHide}
              />
            ))}
          </TabsContent>
        )}

        <TabsContent value="hidden" className="mt-4 space-y-3">
          {loading && <p className="text-zinc-500 text-sm">Loading…</p>}
          {!loading && hiddenOrders.length === 0 && (
            <div className="text-center py-16 text-zinc-500" data-testid="no-hidden-orders">
              Nothing hidden. Completed or cancelled orders you remove will land here so you can restore them.
            </div>
          )}
          {hiddenOrders.map((o) => (
            <OrderCard
              key={o.order_id}
              o={o}
              role={o.buyer_id === user?.user_id ? "buyer" : "vendor"}
              onUpdateStatus={onUpdateStatus}
              onOpenChat={setChatOrder}
              onUnhide={onUnhide}
              onReorder={reorder}
              onGetHelp={setHelpOrder}
              isHidden
            />
          ))}
        </TabsContent>
      </Tabs>

      <OrderChatSheet
        order={chatOrder}
        currentUserId={user?.user_id}
        open={!!chatOrder}
        onOpenChange={(o) => !o && setChatOrder(null)}
      />
      <GetHelpDialog
        order={helpOrder}
        open={!!helpOrder}
        onOpenChange={(o) => !o && setHelpOrder(null)}
        onSubmitted={load}
      />
    </div>
  );
}
